import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography
} from "@mui/material";
import { useRefresh, useShowContext } from "react-admin";
import React, { useCallback, useEffect, useState } from 'react';
import EventBus from "../../EventBus";
import { CustomSnackbarTypes } from "./CustomSnackBar";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import ChefForPartyService from "../../services/ChefForPartyService";
import BookByDishService from "../../services/BookByDishService";
import { ChefForPartyBookingTypeEnum } from "../../enums/ChefForPartyBookingTypeEnum";
import {DatePicker} from "@mui/x-date-pickers";
import moment from "moment";
import ChefForPartyReScheduleReasons from "../../enums/ChefForPartyReScheduleReasons";
import { ChefPartyTimeEnum } from "../../enums/ChefForPartyTimeEnums";
import { getReasonCategory } from "./constants/constant";
import { ActionTypeEnum } from "../../enums/ActionTypeEnum";
import ReasonSubreasonModuleEnum from "../../enums/ReasonSubreasonModuleEnum";
import ChefForPartStatusEnum from "../../enums/ChefForPartStatusEnum";

interface Props {
  data: any;
  open: boolean;
  callback: () => void;
}

const chefForPartyService = new ChefForPartyService();
const bookByDishService = new BookByDishService();

const fmt = (val: number | undefined | null) =>
  val != null ? `₹${val.toLocaleString()}` : "—";

const PriceRow = ({ label, oldVal, newVal }: { label: string; oldVal: any; newVal: any }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>{label}</Typography>
    <Box display="flex" gap={4}>
      <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right' }}>{fmt(oldVal)}</Typography>
      <Typography variant="body2" sx={{ minWidth: 80, textAlign: 'right', fontWeight: 600 }}>{fmt(newVal)}</Typography>
    </Box>
  </Box>
);

const ChefForPartyReScheduleDialog = (props: Props) => {
  const refresh = useRefresh();
  const [rescheduleReasonsOptionMapping,setRescheduleReasonsOptionMapping]=useState<any>([]);
  const [reasonSubCategoryArray, setReasonSubCategoryArray] = useState<string[] | null>(null);
  const [subReason, setSubReason] = useState<string | null>(null)
  const [date, setDate] = useState<any>(null);
  const [time, setTime] = useState<any>(null);
  const [reasonCategory, setReasonCategory] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [couponApplied, setCouponApplied] = useState<boolean>(false);
  const { record } = useShowContext();

  const isBookByDish = record?.bookingType === ChefForPartyBookingTypeEnum.BOOK_BY_DISH;

  const loadData = useCallback(async () => {
    const module = ReasonSubreasonModuleEnum.CHEF_FOR_PARTY;
    switch (record.status) {
      case ChefForPartStatusEnum.CHEF_ALLOTTED:
        setRescheduleReasonsOptionMapping(await getReasonCategory(module ,ActionTypeEnum.POST_ALLOCATION_RESCHEDULED));
        break;
      case ChefForPartStatusEnum.PENDING:
        setRescheduleReasonsOptionMapping(await getReasonCategory(module ,ActionTypeEnum.PRE_ALLOCATION_RESCHEDULED));
        break;
      case ChefForPartStatusEnum.CHEF_CONFIRMED:
        setRescheduleReasonsOptionMapping(await getReasonCategory(module ,ActionTypeEnum.POST_ALLOCATION_RESCHEDULED));
        break;
      case ChefForPartStatusEnum.ONGOING:
        setRescheduleReasonsOptionMapping(await getReasonCategory(module ,ActionTypeEnum.POST_ALLOCATION_RESCHEDULED));
        break;
    }
  }, [record?.status]);

  useEffect(()=>{
    loadData();
  },[loadData])

  const handleClose = () => {
    setReasonCategory(null);
    setSubReason(null);
    setPreviewData(null);
    props.callback();
  };

  const handleReasonCategoryChange = (event: any) => {
    setSubReason(null);
    const selectedCategory: any = rescheduleReasonsOptionMapping.find(
      (option: any) => option.category === event.target.value
    );
    if (selectedCategory) {
      setReasonSubCategoryArray(selectedCategory?.subCategory);
      setReasonCategory(event.target.value);
    }
  };

  function showSubCategoryOrNot(reasonCategory: string) {
    if (reasonCategory?.toLowerCase() === "others" || reasonCategory?.toLowerCase() === 'other') {
      return (
        <FormControl style={{ width: 500 }}>
          <TextField value={subReason} onChange={(e)=>setSubReason(e.target.value)} label="Write a reason" />
        </FormControl>
      );
    } else if (reasonSubCategoryArray?.length !== 0) {
      return (
        <FormControl style={{width:500}}>
          <InputLabel id="select-reason">Select a Subreason</InputLabel>
          <Select
            labelId="select-reason"
            id="select-reason"
            defaultValue="Select a reason"
            value={subReason}
            onChange={(e)=>setSubReason(e.target.value)}
          >
            {reasonSubCategoryArray?.map((val) => (
              <MenuItem key={val} value={val}>{val}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    } else {
      return <></>;
    }
  }

  const validateForm = (): boolean => {
    if (!reasonCategory || !date || !time) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Fill the details to proceed",
        type: CustomSnackbarTypes.WARNING,
      });
      return false;
    }
    if (reasonSubCategoryArray?.length !== 0 && !subReason) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Fill the details to proceed",
        type: CustomSnackbarTypes.WARNING,
      });
      return false;
    }
    if (reasonCategory === ChefForPartyReScheduleReasons.OTHERS && !subReason) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Fill the reason to proceed",
        type: CustomSnackbarTypes.WARNING,
      });
      return false;
    }
    return true;
  };

  const doReschedule = () => {
    if (submitting) return;
    setSubmitting(true);
    const request = isBookByDish
      ? bookByDishService.rescheduleVisit(props?.data?.id, {
          date,
          time,
          rescheduleReason: reasonCategory ?? undefined,
          rescheduleSubReason: subReason ?? undefined,
        })
      : chefForPartyService.reScheduleChefForParty(props?.data?.id, {
          date,
          time,
          rescheduleReason: reasonCategory,
          rescheduleSubReason: subReason ?? undefined,
          applyCoupon: couponApplied,
          ...(couponApplied && previewData?.potentialDiscountCoupon?.discountCouponId
            ? { discountCouponId: previewData.potentialDiscountCoupon.discountCouponId }
            : {}),
          rescheduledBy: 1,
          rescheduledByType: "ADMIN",
        });
    request
      .then(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Reschedule Successfully",
          type: CustomSnackbarTypes.SUCCESS,
        });
        refresh();
        handleClose();
      })
      .catch((err: any) => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: err?.response?.data?.error || err?.response?.data?.message || "Failed to reschedule",
          type: CustomSnackbarTypes.ERROR,
        });
      })
      .finally(() => setSubmitting(false));
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    if (isBookByDish) {
      doReschedule();
      return;
    }
    setSubmitting(true);
    const previewPayload = {
      date,
      time,
      rescheduleReason: reasonCategory,
      rescheduleSubReason: subReason ?? undefined,
      rescheduledBy: 1,
      rescheduledByType: "ADMIN",
      applyCoupon: true,
    };
    chefForPartyService
      .reschedulePreview(props?.data?.id, previewPayload)
      .then((res: any) => {
        setPreviewData(res);
        setCouponApplied(false);
      })
      .catch((err: any) => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: err?.response?.data?.error || err?.response?.data?.message || "Failed to fetch reschedule preview",
          type: CustomSnackbarTypes.ERROR,
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      {/* Step 1 — form dialog */}
      <Dialog
        onClose={handleClose}
        open={props.open && !previewData}
        scroll={"paper"}
        maxWidth="xl"
      >
        <DialogTitle>{"Reschedule Chef for Party"}</DialogTitle>
        <DialogContent>
          <Grid
            container
            spacing={2}
            justifyContent={"space-evenly"}
            style={{ padding: "12px 12px 12px 12px" }}
            direction="column"
            alignItems="center"
          >
            <Grid item xs={12}>
              <FormControl style={{ width: 500 }}>
                <DatePicker
                  label="Reschedule Date"
                  inputFormat="dd MMM yyyy"
                  disableMaskedInput
                  value={date}
                  minDate={new Date()}
                  onChange={(value: any) => {
                    setDate(moment(value).format("YYYY-MM-DD"));
                  }}
                  renderInput={(params: any) => <TextField {...params} style={{marginRight: '12px'}}/>}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl style={{ width: 500 }}>
                <InputLabel id="forTime">Reschedule Time</InputLabel>
                <Select
                  labelId="forTime"
                  id="time"
                  value={time}
                  label="Reschedule Time"
                  onChange={(event: any) => setTime(event.target.value)}
                >
                  {Object.values(ChefPartyTimeEnum).map((e) => (
                    <MenuItem key={e} value={e}>{e}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl style={{ width: 500 }}>
                <InputLabel id="select-label">Tell us Why?</InputLabel>
                <Select
                  labelId="select-label"
                  id="select-label"
                  defaultValue="Tell us Why?"
                  value={reasonCategory}
                  onChange={handleReasonCategoryChange}
                >
                  {rescheduleReasonsOptionMapping.map((item: any) => (
                    <MenuItem key={item.category} value={item.category}>{item.category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {reasonCategory && showSubCategoryOrNot(reasonCategory!)}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ marginTop: "12px" }}>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={handleSubmit}
            size={"large"}
          >
            {submitting ? "Loading…" : "Next"}
          </Button>
          <Button variant="contained" color={"error"} onClick={handleClose} size={"large"}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Step 2 — pricing preview confirmation dialog */}
      {previewData && (
        <Dialog open={!!previewData} onClose={() => setPreviewData(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Reschedule</DialogTitle>
          <DialogContent>
            <Box mb={1}>
              <Box display="flex" justifyContent="flex-end" gap={4} mb={0.5}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right' }}>Current</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80, textAlign: 'right' }}>New</Typography>
              </Box>
              <Divider />
              <PriceRow label="Total Amount" oldVal={previewData?.old?.totalAmount} newVal={previewData?.new?.totalAmount} />
              <PriceRow label="Total Discount" oldVal={previewData?.old?.totalDiscount} newVal={previewData?.new?.totalDiscount} />
              <PriceRow label="Net Payable Amount" oldVal={previewData?.old?.netPayableAmount} newVal={previewData?.new?.netPayableAmount} />
              <PriceRow label="Amount Paid" oldVal={previewData?.old?.amountPaid} newVal={previewData?.new?.amountPaid} />
              <Divider sx={{ mt: 1 }} />
            </Box>

            {previewData?.additionalAmountPayable > 0 && (
              <Box mt={1.5} p={1.5} bgcolor="warning.light" borderRadius={1}>
                <Typography variant="body2" fontWeight={600}>
                  Additional amount payable: {fmt(previewData?.additionalAmountPayable)}
                </Typography>
              </Box>
            )}

            {previewData?.potentialDiscountCoupon && (
              <Box mt={1.5} p={1.5} bgcolor="info.light" borderRadius={1} display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Coupon available: {previewData.potentialDiscountCoupon.couponCode}
                  </Typography>
                  <Typography variant="body2">
                    Discount: {fmt(previewData.potentialDiscountCoupon.totalDiscount)} — Net payable: {fmt(previewData.potentialDiscountCoupon.netPayableAmount)}
                    {previewData?.additionalAmountPayable != null && previewData?.potentialDiscountCoupon?.additionalAmountPayable != null
                      ? ` (saves ${fmt(previewData.additionalAmountPayable - previewData.potentialDiscountCoupon.additionalAmountPayable)})`
                      : ""}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  color={couponApplied ? "error" : "primary"}
                  onClick={() => setCouponApplied((prev) => !prev)}
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {couponApplied ? "Remove Coupon" : "Apply Coupon"}
                </Button>
              </Box>
            )}

            <Box mt={2}>
              <Typography variant="body1" fontWeight={600}>
                Are you sure you want to reschedule?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              disabled={submitting}
              onClick={doReschedule}
            >
              {submitting ? "Submitting…" : "Confirm Reschedule"}
            </Button>
            <Button variant="contained" color="error" onClick={() => setPreviewData(null)}>
              Back
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default ChefForPartyReScheduleDialog;
